import clsx from "clsx";
import {
  CSSProperties,
  FC,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";

import { VirtualList } from "@tsmono/react/virtual";

import { EventNode } from "../types";

import { OutlineLoadingRow, OutlineRow } from "./OutlineRow";
import styles from "./TranscriptOutline.module.css";
import {
  useOutlineCollapse,
  type OutlineCollapseState,
} from "./useOutlineCollapse";
import { resolveOutlineSelection, useOutlineNodes } from "./useOutlineNodes";
import { useOutlineScrollSync } from "./useOutlineScrollSync";
import { useOutlineWidth } from "./useOutlineWidth";

export const outlineNodeRunning = ({
  running,
  backfilling,
  isLast,
}: {
  running: boolean;
  backfilling: boolean;
  isLast: boolean;
}): boolean => running && !backfilling && isLast;

interface TranscriptOutlineProps {
  eventNodes: EventNode[];
  defaultCollapsedIds: Record<string, boolean>;
  running?: boolean;
  /** Whether the sample's event backlog is still loading (live sample). */
  backfilling?: boolean;
  className?: string;
  scrollRef?: RefObject<HTMLDivElement | null>;
  /** The element that actually scrolls the outline (its own overflow
   *  container). Used as the virtual list's scroll parent so virtualization
   *  tracks the outline's internal scroll rather than the shared main
   *  scroller. */
  outlineScrollEl?: HTMLDivElement | null;
  style?: CSSProperties;
  /** Transcript identity scoping the outline's persisted scroll state. Without
   *  it the state lives under a constant key, and hosts that never clear the
   *  property bag leak one transcript's offset into the next. */
  listId?: string;
  /** Name of the agent/subagent currently being displayed. Shown as a static header. */
  agentName?: string;
  /** Reports whether the outline has displayable nodes after filtering. */
  onHasNodesChange?: (hasNodes: boolean) => void;
  /** Called when user clicks an outline item but URL-based navigation is unavailable. */
  onNavigateToEvent?: (eventId: string) => void;
  /** Offset from the top of the scroll container where visible content begins. */
  scrollTrackOffset?: number;

  // --- Callback props replacing store hooks ---
  /** URL generator for deep linking to events. */
  getEventUrl?: (eventId: string) => string | undefined;
  /** Collapse state and callbacks for the outline scope. */
  collapse?: OutlineCollapseState;
  /** Currently selected outline node ID. */
  selectedOutlineId?: string | null;
  /** Set the selected outline node ID. */
  setSelectedOutlineId?: (id: string) => void;
  /** Optional custom link renderer for deep linking (replaces react-router Link). */
  renderLink?: (url: string, children: React.ReactNode) => React.ReactNode;
}

/** The outline list's DOM id and VirtualList persistence-key prefix (the full
 *  key appends the transcript's listId). Exported so the app's per-sample
 *  reset can clear the persisted snapshots by this prefix. */
export const kTranscriptOutlineListKey = "transcript-tree";

// Padding node at the end of the list for breathing room
const EventPaddingNode: EventNode = {
  id: "padding",
  event: {
    event: "info",
    source: "",
    data: "",
    timestamp: "",
    pending: false,
    working_start: 0,
    span_id: null,
    uuid: null,
    metadata: null,
  },
  depth: 0,
  children: [],
};

// Sentinel appended as the final list item while backfilling so the loading
// affordance renders flush after the last outline row (a sibling would sit
// below the list's trailing padding node).
const OutlineLoadingNode: EventNode = { ...EventPaddingNode, id: "loading" };

export const TranscriptOutline: FC<TranscriptOutlineProps> = ({
  eventNodes,
  defaultCollapsedIds,
  running,
  backfilling,
  className,
  scrollRef,
  outlineScrollEl,
  style,
  listId,
  agentName,
  onHasNodesChange,
  onNavigateToEvent,
  scrollTrackOffset,
  getEventUrl,
  collapse,
  selectedOutlineId,
  setSelectedOutlineId,
  renderLink,
}) => {
  const id = kTranscriptOutlineListKey;

  const { collapsedIds, getCollapsed, setCollapsed } = useOutlineCollapse(
    defaultCollapsedIds,
    collapse
  );

  const { outlineNodeList, allNodesList } = useOutlineNodes(
    eventNodes,
    collapsedIds
  );

  const resolvedSelectedId = useMemo(
    () =>
      resolveOutlineSelection(selectedOutlineId, allNodesList, outlineNodeList),
    [selectedOutlineId, allNodesList, outlineNodeList]
  );

  const { onOutlineSelect } = useOutlineScrollSync({
    allNodesList,
    outlineNodeList,
    scrollRef,
    scrollTrackOffset,
    setSelectedOutlineId,
  });

  const hasOutlineNodes = outlineNodeList.length > 0;
  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useEffect(() => {
    onHasNodesChange?.(hasOutlineNodes);
  }, [hasOutlineNodes, onHasNodesChange]);

  // Measure the ideal width for the outline column from label text
  const outlineWidth = useOutlineWidth(outlineNodeList, undefined, agentName);

  // Set --outline-width on the nearest grid ancestor so the column resizes
  // automatically without each app needing to wire up the CSS variable.
  const rootRef = useRef<HTMLDivElement | null>(null);
  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    let ancestor: HTMLElement | null = el.parentElement;
    while (ancestor) {
      if (getComputedStyle(ancestor).display === "grid") {
        ancestor.style.setProperty("--outline-width", `${outlineWidth}px`);
        return;
      }
      ancestor = ancestor.parentElement;
    }
  }, [outlineWidth]);

  const renderRow = useCallback(
    (index: number, node: EventNode) => {
      if (node === EventPaddingNode) {
        return <div key={node.id} style={{ height: "2em" }}></div>;
      } else if (node === OutlineLoadingNode) {
        return <OutlineLoadingRow key={node.id} />;
      } else {
        return (
          <OutlineRow
            node={node}
            key={node.id}
            running={outlineNodeRunning({
              running: running === true,
              backfilling: backfilling === true,
              isLast: index === outlineNodeList.length - 1,
            })}
            selected={
              selectedOutlineId ? resolvedSelectedId === node.id : index === 0
            }
            getEventUrl={getEventUrl}
            onSelect={onOutlineSelect}
            onNavigateToEvent={onNavigateToEvent}
            getCollapsed={getCollapsed}
            setCollapsed={setCollapsed}
            renderLink={renderLink}
          />
        );
      }
    },
    [
      outlineNodeList,
      running,
      backfilling,
      selectedOutlineId,
      resolvedSelectedId,
      getEventUrl,
      onOutlineSelect,
      onNavigateToEvent,
      getCollapsed,
      setCollapsed,
      renderLink,
    ]
  );

  const listData = useMemo(
    () =>
      backfilling
        ? [...outlineNodeList, OutlineLoadingNode, EventPaddingNode]
        : [...outlineNodeList, EventPaddingNode],
    [outlineNodeList, backfilling]
  );

  return (
    <div ref={rootRef} style={style}>
      {agentName && (
        <div
          className={clsx(
            styles.rootHeader,
            "text-size-smaller",
            "text-style-secondary"
          )}
        >
          {agentName}
        </div>
      )}
      <VirtualList<EventNode>
        // Scoped per transcript so a host that never clears the bag can't
        // restore one transcript's offset into another; the "transcript-tree"
        // prefix keeps inspect's per-sample bag clearing matching.
        persistenceKey={listId ? `${id}:${listId}` : id}
        id={id}
        scrollRef={outlineScrollEl ?? null}
        data={listData}
        renderRow={renderRow}
        estimatedItemHeight={50}
        overscan={10}
        embedded={true}
        findScope="none"
        className={clsx(className, "transcript-outline")}
      />
    </div>
  );
};
